/**
 * Hook para validação de slug em tempo real
 * Verifica disponibilidade do slug via RPC check_slug_availability
 * Implementa debounce de 500ms para evitar chamadas excessivas
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

// Tipos de estado da validação
export type SlugValidationStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'error';

// Interface do resultado da validação
interface SlugValidationResult {
  available: boolean;
  reason?: string;
  message?: string;
  normalized?: string;
}

// Interface do hook
interface UseSlugValidationReturn {
  status: SlugValidationStatus;
  message: string;
  normalizedSlug: string;
  checkSlug: (slug: string) => void;
}

/**
 * Gera um slug a partir do nome da empresa
 * Remove acentos, caracteres especiais e converte para lowercase
 */
export const generateSlugFromName = (name: string): string => {
  if (!name) return '';
  
  return name
    .toLowerCase()
    // Remove acentos
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove caracteres especiais exceto letras, números e espaços
    .replace(/[^a-z0-9\s-]/g, '')
    // Substitui espaços por hífens
    .replace(/\s+/g, '-')
    // Remove hífens duplicados
    .replace(/-+/g, '-')
    // Remove hífens no início e fim
    .replace(/^-|-$/g, '')
    // Limita a 50 caracteres
    .substring(0, 50);
};

/**
 * Hook para validação de disponibilidade de slug
 * @param debounceMs - Tempo de debounce em milissegundos (padrão: 500)
 */
export const useSlugValidation = (debounceMs: number = 500): UseSlugValidationReturn => {
  const [status, setStatus] = useState<SlugValidationStatus>('idle');
  const [message, setMessage] = useState<string>('');
  const [normalizedSlug, setNormalizedSlug] = useState<string>('');
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  // Função que efetivamente valida o slug
  const validateSlug = useCallback(async (slug: string) => {
    // Slug vazio = idle
    if (!slug || slug.length < 3) {
      setStatus('idle');
      setMessage(slug ? 'Mínimo de 3 caracteres' : '');
      setNormalizedSlug('');
      return;
    }

    setStatus('checking');
    setMessage('Verificando disponibilidade...');

    try {
      const { data, error } = await supabase.rpc('check_slug_availability', {
        _slug: slug
      });

      if (error) {
        logger.error('[useSlugValidation] Erro ao verificar slug:', error.message);
        setStatus('error');
        setMessage('Erro ao verificar disponibilidade');
        return;
      }

      // Cast seguro para o tipo esperado
      const result = data as unknown as SlugValidationResult;

      if (result && result.available) {
        setStatus('available');
        setMessage('Identificador disponível');
        setNormalizedSlug(result.normalized || slug);
      } else {
        setStatus('unavailable');
        setMessage(result?.message || 'Identificador não disponível');
        setNormalizedSlug(result?.normalized || slug);
      }
    } catch (err) {
      logger.error('[useSlugValidation] Exceção ao verificar slug:', String(err));
      setStatus('error');
      setMessage('Erro ao verificar disponibilidade');
    }
  }, []);

  // Efeito para debounce da validação
  useEffect(() => {
    if (pendingSlug === null) return;

    const timeoutId = setTimeout(() => {
      validateSlug(pendingSlug);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [pendingSlug, debounceMs, validateSlug]);

  // Função exposta para iniciar validação
  const checkSlug = useCallback((slug: string) => {
    setPendingSlug(slug);
    
    // Se slug mudou, reseta para checking imediatamente (feedback visual)
    if (slug && slug.length >= 3) {
      setStatus('checking');
    } else {
      setStatus('idle');
      setMessage(slug ? 'Mínimo de 3 caracteres' : '');
    }
  }, []);

  return {
    status,
    message,
    normalizedSlug,
    checkSlug
  };
};
